import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import Favorite from '../common/Favorite';
import AddToService from '../common/AddToService';
import AddToSuggested from '../common/AddToSuggested';
import Options from '../common/Options';
import SongBadges from '../common/SongBadges';
import ChartProgress from '../common/ChartProgress';
import authService from '../../services/authService';
import { useGlobalContext } from '../../context/GlobalContext';
import { getServicePlayDetails, getServicePlayTimeCategory } from '../../helpers/playHistory';
import { chartStatusOf } from '../../helpers/songFilters';
import { getSongChartCoverage } from '../../utils/partCharts';
import NoContent from '../common/NoContent';
import { ReactComponent as CalendarIcon } from '../../icons/calendar.svg';
import { ReactComponent as ChordBarsIcon } from '../../icons/chord-bars.svg';

import './SongList.scss';

const SongList = ({
  songs,
  popularityData,
  popularitySort = 'none',
  onFav,
  onDel,
  onEdit,
  onAddToService,
  onAddToSuggested,
  suggestedSongs,
  loading,
  activeEditingServiceId,
  isCreatingService,
  songsInServiceIds,
  highlightedSongId,
  accentColor,
  servicePlays,
  tickedServiceLabels,
  showChartProgress = false,
}) => {
  const { t } = useTranslation();
  const [displayAdminActions, setDisplayAdminActions] = useState(false);
  const { canSuggestSongs } = useGlobalContext();
  const songsCount = songs.length;

  const hidePopularityBadge = popularitySort !== 'none';
  const showPlayCount = popularitySort === 'most' || popularitySort === 'least';
  const isServiceDateSort = popularitySort === 'oldest' || popularitySort === 'newest';
  const showUpdatedTime = popularitySort === 'updated';

  useEffect(() => {
    const user = authService.getCurrentUser();

    if (user && user.isAdmin) setDisplayAdminActions(true);
  }, []);

  // How far a charted song has got, as the service card shows it
  const renderChartProgress = (song) => {
    if (!showChartProgress || !chartStatusOf(song)) return null;

    const coverage = getSongChartCoverage(song);

    return (
      <ChartProgress
        className="chart-progress"
        done={coverage.charted}
        total={coverage.total}
        label={(percent) =>
          coverage.charted === coverage.total ? t('charts.charted') : t('charts.percentCharted', { percent })
        }
        doneIcon={<ChordBarsIcon />}
      />
    );
  };

  const renderServicePlayBadge = (song) => {
    // Main resolves the play against the service names ticked in the filter
    const play = servicePlays ? servicePlays.get(song._id) : song.lastServicePlay;
    const details = getServicePlayDetails(play);
    if (!details) {
      if (!isServiceDateSort) return null;

      // Only "never" when it's true of every service, not just the ones ticked in the filter
      const neverAdded = !tickedServiceLabels || !song.lastServicePlay?.serviceTitle;

      return (
        <div className="song-play-history time-never">
          <CalendarIcon className="calendar-icon" />
          {neverAdded && <span>{t('songs.neverAdded')}</span>}
          {!neverAdded && tickedServiceLabels.length === 1 && (
            <span>
              <Trans
                i18nKey="songs.notAddedTo"
                values={{ service: tickedServiceLabels[0] }}
                components={{ service: <span className="service-title" /> }}
              />
            </span>
          )}
          {!neverAdded && tickedServiceLabels.length > 1 && <span>{t('songs.notAddedToSelected')}</span>}
        </div>
      );
    }

    const timeCategory = getServicePlayTimeCategory(play);
    const badgeClasses = classNames('song-play-history', {
      'time-red': timeCategory === 'red',
      'time-yellow': timeCategory === 'yellow',
      'time-green': timeCategory === 'green',
    });

    return (
      <div className={badgeClasses}>
        <CalendarIcon className="calendar-icon" />
        <span>
          <Trans
            i18nKey="songs.addedTo"
            values={{ service: details.serviceTitle, timeAgo: details.timeAgo }}
            components={{ service: <span className="service-title" /> }}
          />
        </span>
      </div>
    );
  };

  const renderSongs = () => {
    const user = authService.getCurrentUser();
    const isAdmin = user && user.isAdmin;
    const showSuggestButton = !isAdmin && canSuggestSongs;

    return songs.map((song) => {
      const isHidden = song.isHidden;
      const songItemClasses = classNames('song-list-item', {
        'with-suggest-button': showSuggestButton,
        'favorite-only': !displayAdminActions && !showSuggestButton,
        'song-highlighted': song._id === highlightedSongId,
        'song-hidden': isHidden && isAdmin,
      });
      const showAddedToServiceBadge =
        activeEditingServiceId ||
        isCreatingService ||
        isServiceDateSort ||
        (showSuggestButton && suggestedSongs && suggestedSongs.length > 0);

      return (
        <li key={song._id} className={songItemClasses}>
          <div className="song-info">
            <p className="song-name">
              <Link to={`/view/${song._id}`}>{song.title}</Link>
            </p>
            <p className="song-artist">{song.artist}</p>
            <SongBadges
              song={song}
              popularityData={popularityData}
              hidePopularityBadge={hidePopularityBadge}
              showPlayCount={showPlayCount}
              showUpdatedTime={showUpdatedTime}
              hideUpdatedBadge={!showUpdatedTime}
              extraBadges={renderChartProgress(song)}
            />
            {showAddedToServiceBadge && renderServicePlayBadge(song)}
          </div>
          <div className="song-actions">
            <Favorite onClick={() => onFav(song)} favorited={song.favorite} />
            {displayAdminActions && (
              <>
                {!isHidden && <AddToService onClick={() => onAddToService(song)} song={song} />}
                <Options
                  item={song}
                  onDelete={onDel}
                  onEdit={onEdit}
                  deleteDisabled={songsInServiceIds?.has(song._id)}
                />
              </>
            )}
            {showSuggestButton && !isHidden && (
              <AddToSuggested onClick={() => onAddToSuggested(song)} song={song} suggestedSongs={suggestedSongs} />
            )}
          </div>
        </li>
      );
    });
  };

  return (
    <div
      className={classNames('SongList', { 'SongList--accented': !!accentColor })}
      style={accentColor ? { '--tag-color': accentColor } : undefined}
    >
      {songsCount === 0 ? <NoContent loading={loading} message={t('songs.noSongs')} /> : <ul>{renderSongs()}</ul>}
    </div>
  );
};

export default SongList;
